import { useMemo } from "react";

import { NumEditComponentProps } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import {
    ARABIC_LETTER_MARK,
    ARABIC_MINUS,
    BIG_NUMBER_DATA_TYPES,
    CLASSIC_SPACE_REGEX,
    NON_BREAKING_SPACE,
} from "./constants";
import { DataTypeSpecificProps } from "./types";

const { groupSeparator, decimalSeparator, isWholeNumberType, getDefaultFormat, getTypeMinMax, removeFormatting } = bridge;

/**
 * Odstraní oddělovače v naformátovaném čísle.
 * Před odstraněním se nahradí mezery za nedělitelné mezery - groupSeparator je obvykle uveden jako nedělitelná mezera.
 * @param value
 */
export const removeGroupSeparator = (value: string): string => (
    value
        .replace(CLASSIC_SPACE_REGEX, NON_BREAKING_SPACE)
        .replace(new RegExp(groupSeparator, "g"), "")
);

/**
 * Nahradí desetinnou tečku za desetinný oddělovač podle aktuální kultury.
 * @param value Řetězec reprezentující číslo v neutrální kultuře.
 */
export const addDecimalSeparator = (value: string): string => (
    value.replace(".", decimalSeparator)
);

/**
 * Mapuje číslice a znaky jiné kultury na standardní číslice.
 * @param value Řetězec reprezentující číselnou hodnotu v jiné kultuře. Jedná se především o hodnoty zadané do NumEditu.
 * @param numbers Pole znaků dané kultury, kde každý znak čísla je umístěn na příslušném indexu. Více info - {@link INumberFormatInfo.numbers}.
 */
export const remapNumbers = (value: string, numbers: readonly string[]): string => {
    const characterMap = new Map(numbers.map((d, i) => [d, String(i)]));
    characterMap.set(ARABIC_MINUS, "-");
    characterMap.set(ARABIC_LETTER_MARK, "");
    const regex = new RegExp(`[${[...numbers, ARABIC_MINUS, ARABIC_LETTER_MARK].join("")}]`, "g");
    return value.replace(regex, char => characterMap.get(char) ?? "");
};

/**
 * Zpracuje props a atributy z elementu na základě datového typu.
 * @param minValueAttribute Atribut reprezentující dolní limit číselné hodnoty.
 * @param maxValueAttribute Atribut reprezentující horní limit číselné hodnoty.
 * @param displayFormatAttribute Formát, v jakém má být hodnota zobrazena.
 * @param dataType Datový typ.
 */
export const useDataTypeSpecificProps = (
    {
        minValue: minValueAttribute,
        maxValue: maxValueAttribute,
        displayFormat: displayFormatAttribute,
        dataType,
    }: Pick<NumEditComponentProps, "maxValue" | "minValue" | "displayFormat" | "dataType">,
): DataTypeSpecificProps => {
    return useMemo(() => {
        if (!dataType) {
            return {
                isWholeNumber: false,
                isBigNumber: false,
                minValue: undefined,
                maxValue: undefined,
                displayFormat: undefined,
            };
        }
        const isBigNumber = BIG_NUMBER_DATA_TYPES.includes(dataType);
        const isWholeNumber = isWholeNumberType(dataType); // nastaví, zda má pracovat pouze s celými čísly
        // nebyl nastaven specifický displayFormat, tak se nastaví podle datového typu
        const displayFormat = displayFormatAttribute ?? getDefaultFormat(dataType);
        const bounds = getTypeMinMax(dataType);

        let minValue = minValueAttribute;
        let maxValue = maxValueAttribute;

        if (bounds) {
            if (!minValue || minValue < bounds.min) {
                minValue = bounds.min;
            }

            if (!maxValue || maxValue > bounds.max) {
                maxValue = bounds.max;
            }
        }
        return {
            isBigNumber,
            isWholeNumber,
            displayFormat,
            minValue,
            maxValue,
        };
    }, [dataType, displayFormatAttribute, maxValueAttribute, minValueAttribute]);
};

/**
 * Odstraní oddělovače tisíců a desetin.
 * V případě že je oddělovač tisíců mezera (dělitelná / nedělitelná), odstraní oba typy mezer.
 * @param value
 */
export const removeNumberFormatting = (value: string) => removeFormatting(value, groupSeparator, decimalSeparator);
